import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Smartphone } from "lucide-react";
import { getMaxViewportOffsetTop } from "@/lib/viewportOffsetTracker";
import { isAppShellActive } from "@/lib/scrollContainer";

type Metrics = Record<string, string>;

const measureSafeTop = () => {
  const probe = document.createElement("div");
  probe.style.cssText = "position:fixed;top:0;left:0;visibility:hidden;pointer-events:none;padding-top:env(safe-area-inset-top,0px)";
  document.body.appendChild(probe);
  const v = getComputedStyle(probe).paddingTop;
  probe.remove();
  return v;
};

/** Sonda fixed top:0/bottom:0 — onde a camada fixed realmente está na tela. */
const measureFixedProbe = () => {
  const probe = document.createElement("div");
  probe.style.cssText = "position:fixed;top:0;bottom:0;left:0;width:1px;visibility:hidden;pointer-events:none";
  document.body.appendChild(probe);
  const r = probe.getBoundingClientRect();
  probe.remove();
  return { top: Math.round(r.top), bottom: Math.round(r.bottom) };
};

const read = (): Metrics => {
  const vv = window.visualViewport;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  const se = document.scrollingElement;
  const container = document.querySelector<HTMLElement>("[data-scroll-container]");
  const fixed = measureFixedProbe();
  return {
    "Altura da janela (innerHeight)": `${window.innerHeight}px`,
    "Altura visível (visualViewport)": vv ? `${Math.round(vv.height)}px` : "indisponível",
    "Deslocamento visível (offsetTop)": vv ? `${Math.round(vv.offsetTop)}px` : "indisponível",
    "Altura da tela (screen)": `${window.screen.height}px`,
    "Espaço do relógio (safe-area top)": measureSafeTop(),
    "Rolagem (scrollY)": `${Math.round(window.scrollY)}px`,
    "Elemento de rolagem": se ? se.tagName.toLowerCase() : "nenhum",
    "Reparo da tela": (() => {
      const s = window.__viewportFixState;
      const extra = [
        `scrollY ${Math.round(window.scrollY)}px`,
        `container ${container ? `${Math.round(container.scrollTop)}px` : isAppShellActive() ? "ausente" : "sem shell"}`,
        `vv.offsetTop ${vv ? Math.round(vv.offsetTop) : "-"}px`,
        `vv.pageTop ${vv ? Math.round(vv.pageTop) : "-"}px`,
        `maior offsetTop na sessão ${getMaxViewportOffsetTop()}px`,
        `sonda fixed top ${fixed.top}px / bottom ${fixed.bottom}px`,
      ].join(", ");
      if (!s) return `inativo — ${extra}`;
      const vvh = s.vvHeight !== null ? `${s.vvHeight}px` : "indisponível";
      return `barra ${s.statusBarMode} — innerHeight ${s.innerHeight}px, screen ${s.screenHeight}px, visualViewport ${vvh}, safe-area top ${s.safeAreaTop}px (${s.reason}) — ${extra}`;
    })(),
    "Navegador (userAgent)": navigator.userAgent,
    "Instalado (iOS standalone)": nav.standalone === true ? "Sim" : "Não",
  };
};

export default function ViewportDiagnosticsCard() {
  const [m, setM] = useState<Metrics>(() => read());
  useEffect(() => {
    const update = () => setM(read());
    const vv = window.visualViewport;
    vv?.addEventListener("resize", update);
    vv?.addEventListener("scroll", update);
    window.addEventListener("scroll", update, { passive: true, capture: true });
    const t = window.setInterval(update, 1000);
    return () => {
      vv?.removeEventListener("resize", update);
      vv?.removeEventListener("scroll", update);
      window.removeEventListener("scroll", update, { capture: true });
      window.clearInterval(t);
    };
  }, []);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Smartphone className="w-5 h-5 text-primary" />
          Tela do aparelho
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {Object.entries(m).map(([k, v]) => (
          <div key={k}>
            <label className="text-xs font-semibold text-muted-foreground uppercase">{k}</label>
            <div className="text-sm font-medium break-words">{v}</div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
